import { isRecord } from "./parseApiId";

export type StoredFile = {
  name: string;
  path?: string;
  url: string;
};

export const lastPathSegment = (value: string) => {
  const parts = value.split("/").filter(Boolean);
  return parts[parts.length - 1] ?? "";
};

export const hasFileExtension = (name: string) => /\.[a-z0-9]+$/i.test(lastPathSegment(name));

export const isJsonFileName = (name: string) => lastPathSegment(name).toLowerCase().endsWith(".json");

export const isXlsxFileName = (name: string) => /\.xlsx?$/i.test(lastPathSegment(name));

export const isLogFileName = (name: string) => lastPathSegment(name).toLowerCase().endsWith(".log");

const stringField = (value: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const field = value[key];
    if (typeof field === "string" && field) return field;
  }
  return "";
};

const parseStoredFile = (value: unknown): null | StoredFile => {
  if (!isRecord(value)) return null;
  const path = stringField(value, ["path"]);
  const name = stringField(value, ["name", "fileName"]) || lastPathSegment(path);
  const url = stringField(value, ["url", "signedUrl", "downloadUrl"]);
  if (!name || (!url && !path)) return null;
  return { name, path: path || undefined, url };
};

const directoryItems = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  if (!isRecord(value)) return [];
  if (Array.isArray(value.files)) return value.files;
  if (Array.isArray(value.list)) return value.list;
  if (Array.isArray(value.data)) return value.data;
  if (isRecord(value.data)) return directoryItems(value.data);
  return [];
};

const parseStoredFolderName = (value: unknown): null | string => {
  if (typeof value === "string") return lastPathSegment(value.replace(/\/+$/, "")) || null;
  if (!isRecord(value)) return null;
  const contentType = typeof value.contentType === "string" ? value.contentType : "";
  const labeledFolder =
    value.isDirectory === true ||
    value.isFolder === true ||
    /directory|folder/i.test(contentType) ||
    (typeof value.name === "string" && value.name.endsWith("/"));
  const url = typeof value.url === "string" ? value.url : "";
  const rawName = typeof value.name === "string" ? value.name.replace(/\/+$/, "") : "";
  const rawPath = typeof value.path === "string" ? value.path.replace(/\/+$/, "") : "";
  const name = lastPathSegment(rawName || rawPath);
  if (!name || hasFileExtension(name)) return null;
  if (!labeledFolder && url && hasFileExtension(name)) return null;
  return name;
};

export const parseStoredDirectory = (value: unknown): { files: StoredFile[]; folders: string[] } => {
  const items = directoryItems(value);
  const data = isRecord(value) ? (isRecord(value.data) ? value.data : value) : {};
  const files = items.flatMap((item) => {
    const parsed = parseStoredFile(item);
    return parsed ? [parsed] : [];
  });
  const explicitFolders = [data.directories, data.folders, data.prefixes].filter(Array.isArray);
  const folderLists = explicitFolders.length > 0 ? explicitFolders : [items];
  const folders = [
    ...new Set(
      folderLists.flatMap((list) => {
        if (!Array.isArray(list)) return [];
        return list.flatMap((item) => {
          const name = parseStoredFolderName(item);
          return name ? [name] : [];
        });
      })
    ),
  ];
  return { files, folders };
};
